<template>
	<div class="conveyancing-form">
		<header v-if="!$integrationOptions.mode && step === 1" class="mb-2">
			<h1 class="alpha heavy center">Generate your no obligation conveyancing quote by selecting the service you require</h1>
		</header>
		<tabbed-form
			ref="tabbedForm"
			v-slot="{ previous, next, goToPrevious, goToNext, goToTab, nextIs }"
			:tabless="true"
			:update-hash="false"
			class="section section--floating"
			tabs-class="tabs--bullets"
			v-on:selectedTabChanged="(data) => selectedTabChanged(data)"
			v-on:tabsChanged="(n) => totalSteps = n.steps.length"
			@submit.prevent="$emit('submit', form)">
			<v-tab
				id="quote-type" v-slot="{ tabIndex }" :exclude-from-tab-steps="!!quoteType"
				:tab-key="1" label="Quote Type">
				<fieldset>
					<div>
						<div>
							<legend><span class="beta heavy">Quote type</span></legend>
						</div>
						<div class="input-group mt-1">
							<label class="quote-type">
								<div v-if="form.errors.get('quote_type')" class="error-block">
									<p v-html="form.errors.get('quote_type')"></p>
								</div>
								<div>
									<label
										:class="quoteTypeClass('sale_purchase')"
										class="quote-type--radio quote-type--radio--buying-and-selling"><span><span>Buying and selling</span> </span>
										<input
											v-model="form.data.data.conveyancing.quote_type" type="radio"
											value="sale_purchase">
									</label>
									<label
										:class="quoteTypeClass('purchase')"
										class="quote-type--radio quote-type--radio--buying"><span><span>Just buying</span> </span>
										<input
											v-model="form.data.data.conveyancing.quote_type" type="radio"
											value="purchase">
									</label>
									<label
										:class="quoteTypeClass('sale')"
										class="quote-type--radio quote-type--radio--selling"><span><span>Just selling</span> </span>
										<input
											v-model="form.data.data.conveyancing.quote_type" type="radio"
											value="sale">
									</label>
									<label
										:class="quoteTypeClass('remortgage')"
										class="quote-type--radio quote-type--radio--remortgaging"><span><span>Remortgaging</span> </span>
										<input
											v-model="form.data.data.conveyancing.quote_type" type="radio"
											value="remortgage">
									</label>
								</div>
							</label>
						</div>
					</div>
				</fieldset>
				<ul class="actions actions--right">
					<li>
						<button
							:aria-disabled="!next"
							:disabled="!next"
							class="button button--primary"
							@click.prevent="goToNext">Next
						</button>
					</li>
				</ul>
			</v-tab>
			<v-tab
				v-if="showSaleTab" id="sale" v-slot="{ tabIndex }" :tab-key="2"
				label="Sale Details">
				<fieldset class="fieldset">
					<legend><span class="beta heavy">About the property you are selling</span>
					</legend>
					<div class="input-group mt-1">
						<div v-if="form.errors.has('data.conveyancing.sale')" class="mb-2 alert alert--danger">
							<p>There are some errors in the form.</p>
						</div>
						<AddressLookup
							v-model="form.data.data.conveyancing.sale.address"
							:errors="form.errors.get('data.conveyancing.sale.address')"
							class="fieldset-section"
							fieldId="sale-property-address"
							label="Sale property address"/>
						<template v-if="!propertyAddressIsOk('sale')">
							<div class="alert alert--danger mt-2">
								<p>Unfortunately, due to Scottish laws relating to conveyancing, we can't currently offer our service for properties being sold in Scotland.</p>
							</div>
						</template>
						<template v-else>
							<label><span>Sale property price </span>
								<money v-model="form.data.data.conveyancing.sale.property_price"/>
								<div
									v-if="form.errors.get('data.conveyancing.sale.property_price')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.sale.property_price')"></p>
								</div>
							</label>
							<label><span>Number of sellers </span>
								<select v-model="form.data.data.conveyancing.sale.number_of_people">
									<option disabled value="">—</option>
									<option value="1">1</option>
									<option value="2">2</option>
									<option value="3">3</option>
									<option value="4">4</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.sale.number_of_people')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.sale.number_of_people')"></p>
								</div>
							</label>
							<label><span>Is the property mortgaged? </span>
								<select v-model="form.data.data.conveyancing.sale.mortgage">
									<option :value="true">Yes</option>
									<option :value="false">No</option>
								</select>
								<div v-if="form.errors.get('data.conveyancing.sale.mortgage')" class="error-block">
									<p v-html="form.errors.get('data.conveyancing.sale.mortgage')"></p>
								</div>
							</label>
							<label
								:class="labelClass(form.data.data.conveyancing.sale.mortgage)"><span>Which lender is the mortgage with? </span>
								<select
									v-model="form.data.data.conveyancing.sale.lender"
									:disabled="isDisabled(form.data.data.conveyancing.sale.mortgage)">
									<option disabled selected value="">—</option>
									<option :value="null">Unknown</option>
									<option
										v-for="(lender, lenderIndex) in form.options.lenders" :value="lender.id"
										v-html="lender.name"/>
								</select>
								<div v-if="form.errors.get('data.conveyancing.sale.lender')" class="error-block">
									<p v-html="form.errors.get('data.conveyancing.sale.lender')"></p>
								</div>
							</label>
							<label><span>Is the property leasehold? </span>
								<select v-model="form.data.data.conveyancing.sale.leasehold">
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div v-if="form.errors.get('data.conveyancing.sale.leasehold')" class="error-block">
									<p v-html="form.errors.get('data.conveyancing.sale.leasehold')"></p>
								</div>
							</label>
							<label><span>Was a purchase scheme used to buy the property? </span>
								<select v-model="form.data.data.conveyancing.sale.has_scheme">
									<!--										<option disabled value="">—</option>-->
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.sale.has_scheme')" class="error-block">
									<p v-html="form.errors.get('data.conveyancing.sale.has_scheme')"></p>
								</div>
							</label>
							<label
								:class="labelClass(form.data.data.conveyancing.sale.has_scheme)"><span>Which purchase scheme was used to buy the property? </span>
								<select
									v-model="form.data.data.conveyancing.sale.scheme"
									:disabled="isDisabled(form.data.data.conveyancing.sale.has_scheme)">
									<option disabled value="">—</option>
									<option value="helpToBuy">Help to Buy</option>
									<option value="helpToBuyIsa">Help to Buy ISA</option>
									<option value="sharedOwnership">Shared Ownership</option>
								</select>
								<div v-if="form.errors.get('data.conveyancing.sale.scheme')" class="error-block">
									<p v-html="form.errors.get('data.conveyancing.sale.scheme')"></p>
								</div>
							</label>
							<div v-if="form.errors.has('data.conveyancing.sale')" class="mt-2 alert alert--danger">
								<p>There are some errors in the form.</p>
							</div>
							<ul class="actions actions--right">
								<li>
									<button
										:aria-disabled="!previous" :disabled="!previous"
										class="button button--secondary"
										v-html="step === 1 ? 'Change Your Quote Type' : 'Back'"
										v-on:click.prevent="goToPrevious"/>
								</li>
								<li>
									<process-button
										:aria-disabled="!next" :disabled="!next" :processing="form.processing"
										class="button button--primary"
										text="Continue"
										@click.prevent="validate('data.conveyancing.sale', goToNext)"/>
								</li>
							</ul>
						</template>
					</div>
				</fieldset>
			</v-tab>
			<v-tab
				v-if="showPurchaseTab" id="purchase" v-slot="{ tabIndex }" :tab-key="3"
				label="Purchase Details">
				<fieldset class="fieldset">
					<legend><span class="beta heavy">About the property you want to buy</span>
					</legend>
					<div class="input-group mt-1">
						<div v-if="form.errors.has('data.conveyancing.purchase')" class="mt-2 alert alert--danger">
							<p>There are some errors in the form.</p>
						</div>
						<AddressLookup
							v-model="form.data.data.conveyancing.purchase.address"
							:errors="form.errors.get('data.conveyancing.purchase.address')"
							class="fieldset-section"
							fieldId="purchase-property-address"
							label="Purchase property address"/>
						<template v-if="!propertyAddressIsOk('purchase')">
							<div class="alert alert--danger mt-2">
								<p>Unfortunately, due to Scottish laws relating to conveyancing, we can't currently offer our service for properties being purchased in Scotland.</p>
							</div>
						</template>
						<template v-else>
							<label><span>Purchase property price </span>
								<money v-model="form.data.data.conveyancing.purchase.property_price"/>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.property_price')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.property_price')"></p>
								</div>
							</label>
							<label><span>Number of purchasers </span>
								<select v-model="form.data.data.conveyancing.purchase.number_of_people">
									<option disabled value="">—</option>
									<option value="1">1</option>
									<option value="2">2</option>
									<option value="3">3</option>
									<option value="4">4</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.number_of_people')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.number_of_people')"></p>
								</div>
							</label>
							<label><span>Will the property be mortgaged? </span>
								<select v-model="form.data.data.conveyancing.purchase.mortgage">
									<option :value="true">Yes</option>
									<option :value="false">No</option>
								</select>
							</label>
							<label
								:class="labelClass(form.data.data.conveyancing.purchase.mortgage)"><span>Which lender will the property be mortgaged with? </span>
								<select
									v-model="form.data.data.conveyancing.purchase.lender"
									:disabled="isDisabled(form.data.data.conveyancing.purchase.mortgage)">
									<option disabled selected value="">—</option>
									<option :value="null">Unknown</option>
									<option
										v-for="(lender, lenderIndex) in form.options.lenders" :value="lender.id"
										v-html="lender.name"/>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.lender')" class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.lender')"></p>
								</div>
							</label>
							<label><span>Are all of the purchasers first time buyers? </span>
								<select v-model="form.data.data.conveyancing.purchase.first_time_buyer">
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.first_time_buyer')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.first_time_buyer')"></p>
								</div>
							</label>
							<label><span>Is the property a second home? </span>
								<select v-model="form.data.data.conveyancing.purchase.second_property">
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.second_property')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.second_property')"></p>
								</div>
							</label>
							<label><span>Is the property a new build? </span>
								<select v-model="form.data.data.conveyancing.purchase.new_build">
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.new_build')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.new_build')"></p>
								</div>
							</label>
							<label><span>Is the property leasehold? </span>
								<select v-model="form.data.data.conveyancing.purchase.leasehold">
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.leasehold')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.leasehold')"></p>
								</div>
							</label>
							<label><span>Is a purchase scheme being used to buy the property? </span>
								<select v-model="form.data.data.conveyancing.purchase.has_scheme">
									<!--										<option disabled value="">—</option>-->
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.has_scheme')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.has_scheme')"></p>
								</div>
							</label>
							<label
								:class="labelClass(form.data.data.conveyancing.purchase.has_scheme)"><span>Which purchase scheme will be used to buy the property? </span>
								<select
									v-model="form.data.data.conveyancing.purchase.scheme"
									:disabled="isDisabled(form.data.data.conveyancing.purchase.has_scheme)">
									<option disabled value="">—</option>
									<option value="helpToBuy">Help to Buy</option>
									<option value="helpToBuyIsa">Help to Buy ISA</option>
									<option value="rightToBuy">Right to Buy</option>
									<option value="sharedOwnership">Shared Ownership</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.purchase.scheme')" class="error-block">
									<p v-html="form.errors.get('data.conveyancing.purchase.scheme')"></p>
								</div>
							</label>
							<div
								v-if="form.errors.has('data.conveyancing.purchase')"
								class="mt-2 alert alert--danger">
								<p>There are some errors in the form.</p>
							</div>
							<ul class="actions actions--right">
								<li>
									<button
										:aria-disabled="!previous" :disabled="!previous"
										class="button button--secondary"
										v-html="step === 1 ? 'Change Your Quote Type' : 'Back'"
										v-on:click.prevent="goToPrevious"/>
								</li>
								<li>
									<process-button
										:aria-disabled="!next" :disabled="!next" :processing="form.processing"
										class="button button--primary"
										text="Continue"
										@click.prevent="validate('data.conveyancing.purchase', goToNext)"/>
								</li>
							</ul>
						</template>
					</div>
				</fieldset>
			</v-tab>
			<v-tab
				v-if="showRemortgageTab" id="remortgage" v-slot="{ tabIndex }" :tab-key="4"
				label="Remortgage Details">
				<fieldset>
					<legend><span class="beta heavy">About the property you want to remortgage</span></legend>
					<div class="input-group mt-1">
						<div
							v-if="form.errors.has('data.conveyancing.remortgage')" class="mt-2 alert alert--danger">
							<p>There are some errors in the form.</p>
						</div>
						<AddressLookup
							v-model="form.data.data.conveyancing.remortgage.address"
							:errors="form.errors.get('data.conveyancing.remortgage.address')"
							class="fieldset-section"
							fieldId="remortgage-property-address"
							label="Remortgage property address"/>
						<template v-if="!propertyAddressIsOk('sale')">
							<div class="alert alert--danger mt-2">
								<p>Unfortunately, due to Scottish laws relating to conveyancing, we can't currently offer our service for properties being sold in Scotland.</p>
							</div>
						</template>
						<template v-else>
							<label><span>Remortgage property price </span>
								<money v-model="form.data.data.conveyancing.remortgage.property_price"/>
								<div
									v-if="form.errors.get('data.conveyancing.remortgage.property_price')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.remortgage.property_price')"></p>
								</div>
							</label>
							<label><span>Number of people involved </span>
								<select v-model="form.data.data.conveyancing.remortgage.number_of_people">
									<option disabled value="">—</option>
									<option value="1">1</option>
									<option value="2">2</option>
									<option value="3">3</option>
									<option value="4">4</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.remortgage.number_of_people')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.remortgage.number_of_people')"></p>
								</div>
							</label>
							<label><span>Which lender is the mortgage with? </span>
								<select
									v-model="form.data.data.conveyancing.remortgage.lender">
									<option disabled selected value="">—</option>
									<!--										<option :value="null">Unknown</option>-->
									<option
										v-for="(lender, lenderIndex) in form.options.lenders" :value="lender.id"
										v-html="lender.name"/>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.remortgage.lender')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.remortgage.lender')"></p>
								</div>
							</label>
							<label><span>Is the property leasehold? </span>
								<select v-model="form.data.data.conveyancing.remortgage.leasehold">
									<!--										<option disabled value="">—</option>-->
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.remortgage.leasehold')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.remortgage.leasehold')"></p>
								</div>
							</label>
							<label><span>Shared ownership? </span>
								<select v-model="form.data.data.conveyancing.remortgage.shared_ownership">
									<!--										<option disabled value="">—</option>-->
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.remortgage.shared_ownership')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.remortgage.shared_ownership')"></p>
								</div>
							</label>
							<label><span>Transfer of equity? </span>
								<select v-model="form.data.data.conveyancing.remortgage.transfer_of_equity">
									<!--										<option disabled value="">—</option>-->
									<option :value="false">No</option>
									<option :value="true">Yes</option>
								</select>
								<div
									v-if="form.errors.get('data.conveyancing.remortgage.transfer_of_equity')"
									class="error-block">
									<p v-html="form.errors.get('data.conveyancing.remortgage.transfer_of_equity')"></p>
								</div>
							</label>
							<div
								v-if="form.errors.has('data.conveyancing.remortgage')"
								class="mt-2 alert alert--danger">
								<p>There are some errors in the form.</p>
							</div>
							<ul class="actions actions--right">
								<li>
									<button
										:aria-disabled="!previous" :disabled="!previous"
										class="button button--secondary"
										v-html="step === 1 ? 'Change Your Quote Type' : 'Back'"
										v-on:click.prevent="goToPrevious"/>
								</li>
								<li>
									<process-button
										:aria-disabled="!next" :disabled="!next" :processing="form.processing"
										class="button button--primary"
										text="Continue"
										@click.prevent="validate('data.conveyancing.remortgage', goToNext)"/>
								</li>
							</ul>
						</template>
					</div>
				</fieldset>
			</v-tab>
			<v-tab v-if="false" id="contact" v-slot="{ tabIndex }" :tab-key="5" label="Contact Details">
				<fieldset>
					<div>
						<div>
							<legend><span class="beta">Contact Details</span></legend>
						</div>
						<div>
							<div class="input-group">
								<!--								<label class="title"><span>Title </span>-->
								<!--									<div v-if="form.errors.get('client.title')" class="error-block">-->
								<!--										<p v-html="form.errors.get('client.title')"></p>-->
								<!--									</div>-->
								<!--									<div>-->
								<!--										<select v-model="form.data.client.title">-->
								<!--											<option disabled value="">—</option>-->
								<!--											<option value="Mr">Mr</option>-->
								<!--											<option value="Mrs">Mrs</option>-->
								<!--											<option value="Ms">Ms</option>-->
								<!--											<option value="Miss">Miss</option>-->
								<!--										</select>-->
								<!--									</div>-->
								<!--								</label>-->
								<label><span>First name </span>
									<div v-if="form.errors.get('client.name_first')" class="error-block">
										<p v-html="form.errors.get('client.name_first')"></p>
									</div>
									<input v-model="form.data.client.name_first" type="text">
								</label>
								<label><span>Last name </span>
									<div v-if="form.errors.get('client.name_last')" class="error-block">
										<p v-html="form.errors.get('client.name_last')"></p>
									</div>
									<input v-model="form.data.client.name_last" type="text">
								</label>
							</div>
							<label><span>Email </span>
								<div v-if="form.errors.get('client.email')" class="error-block">
									<p v-html="form.errors.get('client.email')"></p>
								</div>
								<input v-model="form.data.client.email" type="text">
							</label>
							<label><span>Email Confirmation </span>
								<input
									v-model="form.data.client.email_confirmation" onpaste="return false;"
									type="text">
							</label>
							<label><span>Phone </span>
								<div v-if="form.errors.get('client.phone')" class="error-block">
									<p v-html="form.errors.get('client.phone')"></p>
								</div>
								<input v-model="form.data.client.phone" type="text">
							</label>
						</div>
					</div>
				</fieldset>
				<ul class="actions actions--right">
					<li>
						<button
							:aria-disabled="!previous" :disabled="!previous" class="button button--secondary"
							v-on:click.prevent="goToPrevious">Back
						</button>
					</li>
					<li>
						<button
							:aria-disabled="!next" :disabled="!next" class="button button--primary"
							v-on:click.prevent="goToTab('review')">Review Your Details
						</button>
					</li>
				</ul>
			</v-tab>
			<v-tab
				id="review" v-slot="{ tabIndex }" :tab-key="6" class="comparison-form--conveyancing__review"
				label="Review">
				<header>
					<h1 class="beta">Review Your Details</h1>
				</header>
				<section class="mt-2">
					<accordion-group>
						<accordion v-if="false" :show="true">
							<template #title>
								<h2>Contact Details</h2>
							</template>
							<div class="detail-list my-1">
								<dl class="detail-list__item">
									<dt>Name</dt>
									<dd v-html="$present('fullName', form.data.client)"/>
								</dl>
								<dl class="detail-list__item">
									<dt>Email</dt>
									<dd v-html="form.data.client.email"/>
								</dl>
								<dl class="detail-list__item--icon">
									<dt>Phone</dt>
									<dd v-html="form.data.client.phone"/>
								</dl>
							</div>
						</accordion>
						<accordion
							v-if="['sale', 'sale_purchase'].indexOf(form.data.data.conveyancing.quote_type) > -1">
							<template #title>
								<h2>Sale Property Details</h2>
							</template>
							<dl class="detail-list my-1">
								<div class="detail-list__item">
									<dt>Sale property address</dt>
									<dd>
										<Address
											v-if="form.data.data.conveyancing.sale.address"
											:address="form.data.data.conveyancing.sale.address"/>
									</dd>
								</div>
								<div
									v-if="form.data.data.conveyancing.sale.property_price"
									class="detail-list__item">
									<dt>Sale property price</dt>
									<dd v-html="$present('price', form.data.data.conveyancing.sale.property_price)"/>
								</div>
								<div class="detail-list__item">
									<dt>Number of sellers</dt>
									<dd v-html="form.data.data.conveyancing.sale.number_of_people"/>
								</div>
								<div class="detail-list__item--icon">
									<dt>Sale property is mortgaged</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.sale.mortgage"/>
									</dd>
								</div>
								<div v-if="form.data.data.conveyancing.sale.mortgage" class="detail-list__item">
									<dt>Sale property mortgage lender</dt>
									<dd v-if="!form.data.data.conveyancing.sale.lender">Unknown</dd>
									<dd
										v-if="form.data.data.conveyancing.sale.lender"
										v-html="lenderName(form.data.data.conveyancing.sale.lender)"/>
								</div>
								<div class="detail-list__item--icon">
									<dt>Sale property is leasehold</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.sale.leasehold"/>
									</dd>
								</div>
								<div class="detail-list__item--icon">
									<dt>Sale property was purchased via a purchase scheme</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.sale.has_scheme"/>
									</dd>
								</div>
								<div v-if="form.data.data.conveyancing.sale.has_scheme" class="detail-list__item">
									<dt>Sale property purchase scheme</dt>
									<dd v-if="'helpToBuy' === form.data.data.conveyancing.sale.scheme">Help to Buy</dd>
									<dd v-if="'helpToBuyIsa' === form.data.data.conveyancing.sale.scheme">Help to Buy ISA</dd>
									<dd v-if="'sharedOwnership' === form.data.data.conveyancing.sale.scheme">Shared Ownership</dd>
								</div>
							</dl>
						</accordion>
						<accordion
							v-if="['purchase', 'sale_purchase'].indexOf(form.data.data.conveyancing.quote_type) > -1">
							<template #title>
								<h2>Purchase Property Details</h2>
							</template>
							<dl class="detail-list my-1">
								<div class="detail-list__item">
									<dt>Purchase property address</dt>
									<dd>
										<Address
											v-if="form.data.data.conveyancing.purchase.address"
											:address="form.data.data.conveyancing.purchase.address"/>
									</dd>
								</div>
								<div
									v-if="form.data.data.conveyancing.purchase.property_price"
									class="detail-list__item">
									<dt>Purchase property price</dt>
									<dd v-html="$present('price', form.data.data.conveyancing.purchase.property_price)"/>
								</div>
								<div class="detail-list__item">
									<dt>Number of purchasers</dt>
									<dd v-html="form.data.data.conveyancing.purchase.number_of_people"/>
								</div>
								<div class="detail-list__item--icon">
									<dt>Purchase property will be mortgaged</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.purchase.mortgage"/>
									</dd>
								</div>
								<div v-if="form.data.data.conveyancing.purchase.mortgage" class="detail-list__item">
									<dt>Purchase property mortgage lender</dt>
									<dd v-if="!form.data.data.conveyancing.purchase.lender">Unknown</dd>
									<dd
										v-if="form.data.data.conveyancing.purchase.lender"
										v-html="lenderName(form.data.data.conveyancing.purchase.lender)"/>
								</div>
								<div class="detail-list__item--icon">
									<dt>Purchase includes first time buyers</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.purchase.first_time_buyer"/>
									</dd>
								</div>
								<div class="detail-list__item--icon">
									<dt>Purchase property is a second home</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.purchase.second_property"/>
									</dd>
								</div>
								<div class="detail-list__item--icon">
									<dt>Purchase property is a new build</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.purchase.new_build"/>
									</dd>
								</div>
								<div class="detail-list__item--icon">
									<dt>Purchase property is leasehold</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.purchase.leasehold"/>
									</dd>
								</div>
								<div class="detail-list__item--icon">
									<dt>Purchase property will be purchased via a purchase scheme</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.purchase.has_scheme"/>
									</dd>
								</div>
								<div
									v-if="form.data.data.conveyancing.purchase.has_scheme"
									class="detail-list__item">
									<dt>Purchase property purchase scheme</dt>
									<dd v-if="'helpToBuy' === form.data.data.conveyancing.purchase.scheme">Help to Buy</dd>
									<dd v-if="'helpToBuyIsa' === form.data.data.conveyancing.purchase.scheme">Help to Buy ISA</dd>
									<dd v-if="'rightToBuy' === form.data.data.conveyancing.purchase.scheme">Right to Buy</dd>
									<dd v-if="'sharedOwnership' === form.data.data.conveyancing.purchase.scheme">Shared Ownership</dd>
								</div>
							</dl>
						</accordion>
						<accordion
							v-if="['remortgage'].indexOf(form.data.data.conveyancing.quote_type) > -1">
							<template #title>
								<h2 class="beta">Remortgage Details</h2>
							</template>
							<dl class="detail-list my-1">
								<div class="detail-list__item">
									<dt>Remortgage property address</dt>
									<dd>
										<Address
											v-if="form.data.data.conveyancing.remortgage.address"
											:address="form.data.data.conveyancing.remortgage.address"/>
									</dd>
								</div>
								<div
									v-if="form.data.data.conveyancing.remortgage.property_price"
									class="detail-list__item">
									<dt>Remortgage property price</dt>
									<dd v-html="$present('price', form.data.data.conveyancing.remortgage.property_price)"/>
								</div>
								<div class="detail-list__item">
									<dt>Number of people invloved</dt>
									<dd v-html="form.data.data.conveyancing.remortgage.number_of_people"/>
								</div>
								<div class="detail-list__item">
									<dt>Remortgage lender</dt>
									<dd
										v-if="form.data.data.conveyancing.remortgage.lender"
										v-html="lenderName(form.data.data.conveyancing.remortgage.lender)"/>
								</div>
								<div class="detail-list__item--icon">
									<dt>Property is leasehold</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.remortgage.leasehold"/>
									</dd>
								</div>
								<div class="detail-list__item--icon">
									<dt>Shared ownership</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.remortgage.shared_ownership"/>
									</dd>
								</div>
								<div class="detail-list__item--icon">
									<dt>Transfer of equity</dt>
									<dd>
										<bool-icon :value="form.data.data.conveyancing.remortgage.transfer_of_equity"/>
									</dd>
								</div>
							</dl>
						</accordion>
					</accordion-group>
				</section>
				<ul class="actions actions--right">
					<li>
						<button
							:aria-disabled="!previous" :disabled="!previous"
							class="button button--secondary"
							v-on:click.prevent="goToPrevious()">Back
						</button>
					</li>
					<li class="wide">
						<process-button
							:processing="form.processing"
							class="button button--primary--wide" text="Get Your Quote"/>
					</li>
				</ul>
			</v-tab>
			<v-tab id="privacy" v-slot="{ tabIndex }" :exclude-from-tab-steps="true" :tab-key="7" label="Privacy">
				<section class="disclaimer alert alert--warning">
					<header>
						<h1>Your Privacy &amp; Your Data</h1>
					</header>
					<div class="mt-2">
						<div class="editorial">
							<p>Your data will be processed in accordance with GDPR regulations and in accordance to the privacy policy and terms &amp; conditions of this website, given that by requesting your quote(s), you are consenting to the processing of your data for the purpose of calculating your quote(s).</p>
							<!--						<p>By providing your details, some of our solicitors and licensed conveyancers may contact you via email or telephone to discuss their quote in more detail and to answer any questions that you might have regarding their service and the conveyancing process.</p>-->
						</div>
					</div>
				</section>
				<ul class="actions actions--right">
					<li>
						<button
							:aria-disabled="!previous" :disabled="!previous" class="button button--secondary"
							v-on:click.prevent="goToPrevious">Back
						</button>
					</li>
				</ul>
			</v-tab>
		</tabbed-form>
	</div>
</template>
<script>
import TabbedForm from "../../../common/components/forms/tabbed-form.vue";
import Accordion from "../../../common/components/v-accordion/Accordion.vue";
import AccordionGroup from "../../../common/components/v-accordion/AccordionGroup.vue";
import Address from "../../../components/Address.vue";
import AddressLookup from "../../../components/forms/fields/AddressLookup.vue";
import scrollTo from "../../../mixins/scrollTo";

export default {
	components: {
		Accordion,
		AccordionGroup,
		Address,
		AddressLookup,
		TabbedForm,
	},

	mixins: [
		scrollTo,
	],

	data() {
		return {
			step: null,
			totalSteps: null,
			selectedTab: null,
			tabIsAStep: null,
		};
	},

	computed: {
		tabs() {
			return this.$refs.tabs;
		},
		showSaleTab() {
			return ["sale", "sale_purchase"].indexOf(this.form.data.data.conveyancing.quote_type) > -1;
		},
		showPurchaseTab() {
			return ["purchase", "sale_purchase"].indexOf(this.form.data.data.conveyancing.quote_type) > -1;
		},
		showRemortgageTab() {
			return ["remortgage"].indexOf(this.form.data.data.conveyancing.quote_type) > -1;
		},
		quoteTypeDisplay() {
			return {
				sale_purchase: "Sale & Purchase",
				purchase: "Purchase",
				sale: "Sale",
				remortgage: "Remortgage",
			}[this.form.data.data.conveyancing.quote_type];
		},
	},

	methods: {
		quoteTypeClass(quoteType) {
			return {
				"quote-type--radio--selected": quoteType === this.form.data.data.conveyancing.quote_type,
			};
		},
		lenderName(lenderId) {
			return _.find(this.form.options.lenders, function (lender) {
				return lender.id === lenderId;
			}).name;
		},
		selectedTabChanged(data) {
			this.selectedTab = data.selectedTab.id;
			this.step = data.step;
			this.tabIsAStep = data.tabIsAStep;
			this.resetScroll();
			this.tellParentToScrollToIFrame();
		},
		labelClass(field) {
			return {
				disabled: this.isDisabled(field),
			};
		},
		isDisabled(field) {
			return field !== true;
		},
		propertyAddressIsOk(service) {
			let address = this.form.getData("data.conveyancing." + service + ".address");
			return address?.address_country !== "Scotland";
		},
		validate(section, callback) {
			this.form.validate(section).post(this.route("/quotes/services/:service", this.$route.params)).then(response => {
				callback();
			});
		},
	},

	props: {
		form: {
			required: true,
			type: Object,
		},
		processing: {
			required: true,
		},
		error: {
			required: false,
		},
		quoteType: {
			required: false,
		},
	},

	watch: {
		error: {
			handler: function (error) {
				if (!error) {
					this.form.errors.clear();
					return;
				}

				this.refs.tabbedForm.refs.tabs.goToTab("quote-type");
				this.form.fail(error);
			},
		},
		form: {
			deep: true,
			handler(formData, oldFormData) {
				if (formData.data.data.conveyancing.quote_type !== oldFormData.data.data.conveyancing.quote_type) {
					this.form.reset();
				}
			},
		},
	},

	mounted() {
		this.tellParentToReport("quoteFormDisplayed");

		this.form.set("data.conveyancing.quote_type", this.$route.query.quoteType || "");

		if (this.form.data.data.conveyancing.quote_type) {
			this.$nextTick(() => {
				this.refs.tabbedForm.refs.tabs.goToNext();
			});
		}
	},
};
</script>
